/* eslint-disable hh/comment-budget */
import { get, keyBy, startCase } from 'lodash'
import { useMemo } from 'react'

import { useFetchConfiguredServiceDetailsQuery } from 'api/configured-service/configuredServiceCrud'
import { useGetContainersQuery } from 'api/inventory/containerCrud'
import { EVERY_CONTAINER_ROW } from 'api/inventory/containerListFlags'

export type ContainerInfoChip = { label: string; color?: string; measureUnit?: string }
export type ContainerInfoFields = { serial: string; lines: string[]; chips: ContainerInfoChip[] }

// Loose shape of the fields shapeContainerInfo reads off a ContainerResponse —
// every field optional/nullable since the record is external (untyped query).
type ContainerRecordLike = {
  serialNumber?: string | null
  manufacturer?: string | null
  status?: string | null
  condition?: string | null
  account?: { accountName?: string | null } | null
  location?: { line1?: string | null; city?: string | null; state?: string | null; zipCode?: string | null } | null
  measure?: { volume?: number | null; unit?: string | null } | null
  containerType?: { name?: string | null } | null
}
type ServiceLike = { serviceName?: string | null } | null | undefined

// Pure shaper — turns a container record (+ its configured service) into the
// label-less { serial, lines, chips } the popover renders. Exported for tests.
export const shapeContainerInfo = (container?: ContainerRecordLike | null, service?: ServiceLike): ContainerInfoFields => {
  if (!container) return { serial: '', lines: [], chips: [] }
  const { measure, location, status, condition } = container
  const size = measure ? [measure.volume, measure.unit].filter(value => value != null && value !== '').join(' ') : ''
  const address = location
    ? [location.line1, location.city, [location.state, location.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : ''
  // No labels — values read in context, like the stop popover. Serial is surfaced
  // separately so the popover can render it with its own icon.
  const serial = container.serialNumber ?? ''
  const lines = [container.account?.accountName ?? '', address, service?.serviceName ?? '']
  // measureUnit drives the icon'd MeasureChip rendering in the popover.
  const chips: ContainerInfoChip[] = [
    { label: container.containerType?.name ?? '' },
    { label: size, measureUnit: measure?.unit ?? '' },
    { label: status ? startCase(String(status).toLowerCase()) : '' },
    { label: condition ? startCase(String(condition).toLowerCase()) : '' },
    { label: container.manufacturer ?? '' },
  ]
  return { serial, lines, chips }
}

// Shapes a container's inventory record into the label-less { serial, lines, chips }
// the ContainerInfoPopover renders. Accepts either a full container record (the
// inventory grid row already is a ContainerResponse) or a container id. Ids are
// resolved against the shared container list (GET /api/v1/containers, the same
// source the inventory page loads) keyed by id, so the cluster and master maps
// get full data for every container — including ones missing from the route
// geojson — from one cached request instead of a per-hover detail fetch.
export const useContainerInfoFields = (containerOrId?: unknown): ContainerInfoFields => {
  const isRecord = containerOrId != null && typeof containerOrId === 'object'
  const id = isRecord ? null : (containerOrId as string | undefined)
  // Id callers resolve against the full container list; record callers (inventory)
  // already hold the record, so skip the request entirely.
  const { data: listData } = useGetContainersQuery(EVERY_CONTAINER_ROW, { skip: isRecord || !id })
  // Index the list by id once per query result so the per-hover lookup is O(1).
  const containersById = useMemo(() => keyBy(get(listData, 'containers') || [], 'id'), [listData])
  const container = useMemo(() => {
    if (isRecord) return containerOrId
    if (!id) return null
    return containersById[id] || null
  }, [isRecord, containerOrId, id, containersById])

  // The container record carries the configured-service id(s); resolve the first to its name.
  const serviceId = (get(container, 'services') || [])[0]
  const { data: service } = useFetchConfiguredServiceDetailsQuery({ id: serviceId }, { skip: !serviceId })

  return useMemo<ContainerInfoFields>(() => shapeContainerInfo(container, service), [container, service])
}

export default useContainerInfoFields
